/**
 * ProjectCard - Enhanced Premium Project Card Component
 * 
 * A sophisticated card component for displaying property development projects.
 * Enhanced with premium styling for high-end real estate presentation.
 * 
 * Features:
 * - Elegant hover effects
 * - Premium typography
 * - Status indicators
 * - Availability tracking
 * - Custom color theming
 */

'use client';

import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import Image from 'next/image';
import Link from 'next/link';

export interface ProjectCardProps {
  project: PropertyDevelopmentProject;
  href: string;
  brandLogo?: string;
  brandColors?: {
    primary?: string;
    accent?: string;
  };
  messages: {
    availableUnits: string;
    soldOut: string;
    comingSoon: string;
    enquireNow: string;
    viewProject: string;
    startingFrom: string;
  };
  locale?: string;
  className?: string;
}

/**
 * Enhanced ProjectCard Component
 */
export function ProjectCard({
  project,
  href,
  brandLogo,
  brandColors,
  messages,
  locale: _locale = 'en',
  className = '',
}: ProjectCardProps) {
  const colors = {
    primary: brandColors?.primary || '#243462',
    accent: brandColors?.accent || '#BBA339',
  };

  const isSoldOut = project.status === 'SOLD_OUT' || project.availableUnits === 0;
  const isComingSoon = project.status === 'UPCOMING';

  // Calculate availability percentage
  const availabilityPercentage = project.totalUnits && project.soldUnits
    ? ((project.totalUnits - project.soldUnits) / project.totalUnits) * 100
    : 0;

  return (
    <Link
      href={href}
      className={`group relative block overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${className}`}
    >
      {/* Image Container - Larger for More Impact */}
      <div className="relative h-[400px] overflow-hidden">
        {/* Background Image */}
        <div className="h-full w-full">
          {project.heroImageUrl ? (
            <Image
              src={project.heroImageUrl}
              alt={project.name}
              fill
              className="object-cover transition-transform duration-700 group-hover:scale-105"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          ) : (
            <div
              className="h-full w-full bg-gradient-to-br"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
              }}
            />
          )}
        </div>

        {/* Elegant Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

        {/* Top Row - Logo and Status */}
        <div className="absolute left-0 right-0 top-0 z-10 flex items-start justify-between p-6">
          {/* Brand Logo */}
          {brandLogo && (
            <div className="rounded-xl bg-white/95 p-3 shadow-xl backdrop-blur-sm">
              <Image
                src={brandLogo}
                alt={`${project.name} logo`}
                width={100}
                height={40}
                className="h-auto w-24 object-contain"
              />
            </div>
          )}

          {/* Status Badge */}
          <div className="ml-auto">
            {isSoldOut ? (
              <span className="inline-flex items-center rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-xl backdrop-blur-md">
                {messages.soldOut}
              </span>
            ) : isComingSoon ? (
              <span
                className="inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold text-white shadow-xl backdrop-blur-md"
                style={{ backgroundColor: colors.accent }}
              >
                {messages.comingSoon}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-xl backdrop-blur-md">
                {project.availableUnits} {messages.availableUnits}
              </span>
            )}
          </div>
        </div>

        {/* Bottom Overlay - Project Type */}
        {project.projectType && (
          <div className="absolute bottom-6 left-6 z-10">
            <span
              className="inline-flex items-center rounded-lg bg-white/90 px-4 py-2 text-xs font-bold uppercase tracking-wider backdrop-blur-md"
              style={{ color: colors.primary }}
            >
              {project.projectType.replace(/_/g, ' ')}
            </span>
          </div>
        )}
      </div>

      {/* Content Section - Enhanced Layout */}
      <div className="p-8">
        {/* Project Name */}
        <h2
          className="mb-3 text-3xl font-bold leading-tight transition-colors group-hover:opacity-80"
          style={{ color: colors.primary }}
        >
          {project.name}
        </h2>

        {/* Description */}
        {project.description && (
          <p className="mb-6 line-clamp-3 text-base leading-relaxed text-gray-600">
            {project.description}
          </p>
        )}

        {/* Divider */}
        <div className="mb-6 h-px bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />

        {/* Info Grid */}
        <div className="mb-6 grid grid-cols-2 gap-6">
          {/* Price */}
          {project.displayPriceText && (
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                {messages.startingFrom}
              </div>
              <div
                className="text-2xl font-bold"
                style={{ color: colors.accent }}
              >
                {project.displayPriceText}
              </div>
            </div>
          )}

          {/* Units */}
          {project.totalUnits && (
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Total Units
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {project.totalUnits}
              </div>
            </div>
          )}
        </div>

        {/* Unit Mix Summary */}
        {project.unitMixSummary && (
          <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span>{project.unitMixSummary}</span>
          </div>
        )}

        {/* Availability Progress Bar */}
        {!isSoldOut && project.totalUnits && (
          <div className="mb-6">
            <div className="mb-2 flex justify-between text-sm font-medium text-gray-600">
              <span>{project.availableUnits} Available</span>
              <span className="font-bold" style={{ color: colors.accent }}>
                {Math.round(availabilityPercentage)}%
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${availabilityPercentage}%`,
                  backgroundColor: colors.accent,
                }}
              />
            </div>
          </div>
        )}

        {/* Bottom Section - Stage and CTA */}
        <div className="flex items-center justify-between border-t border-gray-200 pt-6">
          {/* Stage */}
          {project.stage && (
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: colors.accent }} />
              <span className="text-sm font-medium text-gray-600">
                {project.stage.replace(/_/g, ' ')}
              </span>
            </div>
          )}

          {/* CTA Arrow */}
          <div className="flex items-center gap-2 text-sm font-semibold transition-all group-hover:gap-3" style={{ color: colors.primary }}>
            <span>{messages.viewProject}</span>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>
      </div>

      {/* Hover Border Glow */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 ring-2 ring-inset transition-opacity duration-300 group-hover:opacity-100"
        style={{ borderColor: `${colors.accent}40` }}
      />
    </Link>
  );
}

// Note: heroImageUrl is now part of the main PropertyDevelopmentProject interface
